import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { accountsAPI, chatbotsAPI, moderatorsAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, 
  Plus, 
  Settings,
  Trash2,
  Loader2,
  MoreVertical,
  Copy,
  Database,
  Search,
  Users,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { HelpButton } from '@/components/common/HelpButton';
import { DisplayIcon } from '@/components/common/IconSelector';
import CreateAgentWizard from '@/components/wizard/CreateAgentWizard';

export const ChatbotsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { featureAccess, user } = useAuth();
  const isModeratorOnly = !!user?.is_moderator_only;
  const [chatbots, setChatbots] = useState([]);
  const [filteredChatbots, setFilteredChatbots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteChatbot, setDeleteChatbot] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshInvites = async () => {
    setRefreshing(true);
    try {
      // Hitting /auth/me self-heals any newly-assigned moderator invites,
      // then we reload the chatbot list so the new agents appear.
      const { authAPI } = await import('@/services/api');
      await authAPI.getMe();
      await loadData();
      toast.success('Checked for new assignments');
    } catch (e) {
      toast.error('Could not check for invites');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-open wizard when arriving with ?new=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setShowCreateDialog(true);
      // Clean the URL so refresh doesn't re-open the wizard
      navigate('/dashboard/chatbots', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    // Filter chatbots based on search query
    if (searchQuery.trim() === '') {
      setFilteredChatbots(chatbots);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = chatbots.filter(chatbot => 
        chatbot.name.toLowerCase().includes(query) ||
        (chatbot.description && chatbot.description.toLowerCase().includes(query))
      );
      setFilteredChatbots(filtered);
    }
  }, [searchQuery, chatbots]);

  const loadData = async () => {
    try {
      const [chatbotsRes, accountsRes] = await Promise.all([
        chatbotsAPI.listAll(),
        accountsAPI.list()
      ]);
      setChatbots(chatbotsRes.data);
      setFilteredChatbots(chatbotsRes.data);
      setAccounts(accountsRes.data);
      
      // Load sub-accounts for all accounts
      const subAccountPromises = accountsRes.data.map(async (account) => {
        const res = await accountsAPI.listSubAccounts(account.account_id);
        return { accountId: account.account_id, subAccounts: res.data };
      });
      
      const subAccountResults = await Promise.all(subAccountPromises);
      const subAccountMap = {};
      subAccountResults.forEach(({ accountId, subAccounts }) => {
        subAccountMap[accountId] = subAccounts;
      });
      setSubAccounts(subAccountMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load AI Employees');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChatbot = async () => {
    if (!deleteChatbot) return;
    
    try {
      await chatbotsAPI.delete(
        deleteChatbot.account_id,
        deleteChatbot.sub_account_id,
        deleteChatbot.chatbot_id
      );
      toast.success('AI Employee deleted successfully');
      setDeleteChatbot(null);
      loadData();
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      toast.error('Failed to delete AI Employee');
    }
  };

  const copyEmbedCode = (chatbot) => {
    navigator.clipboard.writeText(chatbot.embed_code || '');
    toast.success('Embed code copied to clipboard');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'draft': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'paused': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="chatbots-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">AI Employees</h1>
          <p className="text-slate-600 mt-1">Create and manage your AI employees</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <HelpButton 
            title="AI Employees"
            content={`
              <p>AI Employees are intelligent assistants trained on your knowledge base.</p>
              <ul>
                <li><strong>Create:</strong> Click "New AI Employee" to build a new assistant</li>
                <li><strong>Configure:</strong> Click on an agent to add documents and customize settings</li>
                <li><strong>Embed:</strong> Get embed code to add the agent to your website</li>
                <li><strong>Moderators:</strong> Add up to 2 moderators per agent to help manage it</li>
              </ul>
              <p class="mt-2"><strong>Tips:</strong></p>
              <ul>
                <li>Upload relevant documents to improve answer quality</li>
                <li>Customize the welcome message for your brand</li>
                <li>Use icons to easily identify different agents</li>
              </ul>
            `}
          />
          <Button
            onClick={handleRefreshInvites}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 w-full sm:w-auto"
            data-testid="refresh-assigned-agents-btn"
            disabled={refreshing}
            title="Check for newly assigned AI agents from invites"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking…' : 'Check for new invites'}
          </Button>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            data-testid="create-chatbot-btn"
            disabled={isModeratorOnly}
            title={isModeratorOnly ? 'Moderator accounts cannot create AI Employees.' : ''}
            style={isModeratorOnly ? { display: 'none' } : undefined}
          >
            <Plus className="w-4 h-4 mr-2" />
            New AI Employee
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search AI agents by name or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-11"
          data-testid="chatbot-search-input"
        />
      </div>

      {/* AI Employees Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : filteredChatbots.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Bot className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {searchQuery ? 'No AI agents found' : 'No AI agents yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchQuery 
                ? `No AI agents match "${searchQuery}"`
                : 'Create your first AI agent to get started'
              }
            </p>
            {!searchQuery && !isModeratorOnly && (
              <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create AI Employee
              </Button>
            )}
            {!searchQuery && isModeratorOnly && (
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                You haven&apos;t been added as a moderator to any AI Employee yet.
                Ask the account owner to invite you from their AI Employee → Moderators tab.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        (() => {
          // Split into Owned vs Moderating so the page becomes a clean
          // two-tier workspace for accounts that both own agents and
          // moderate others (the "Moderator with subscription" case in
          // the spec). Backend annotates each row with `user_role`.
          const ownedList = filteredChatbots.filter((c) => (c.user_role || 'owner') !== 'moderator');
          const moderatingList = filteredChatbots.filter((c) => c.user_role === 'moderator');

          const renderCard = (chatbot) => {
            const isMod = chatbot.user_role === 'moderator';
            return (
            <Card
              key={chatbot.chatbot_id}
              className="bg-white border-slate-200 shadow-sm hover:border-blue-300 transition-all group cursor-pointer"
              onClick={() => navigate(`/dashboard/chatbots/${chatbot.chatbot_id}`, { state: { chatbot } })}
              data-testid={`chatbot-card-${chatbot.chatbot_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <DisplayIcon iconName={chatbot.icon || 'Bot'} color={chatbot.icon_color || 'blue'} size="lg" />
                  <div className="flex items-center gap-2">
                    {isMod && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200" title="You moderate this AI Employee — the owning organisation controls it.">
                        Moderator
                      </Badge>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border-slate-200">
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/chatbots/${chatbot.chatbot_id}`, { state: { chatbot } }); }}
                          className="text-slate-700 focus:text-slate-900 focus:bg-slate-100"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {isMod ? 'Open' : 'Configure'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/chatbots/${chatbot.chatbot_id}`, { state: { chatbot, tab: 'knowledge' } }); }}
                          className="text-slate-700 focus:text-slate-900 focus:bg-slate-100"
                        >
                          <Database className="w-4 h-4 mr-2" />
                          Knowledge Base
                        </DropdownMenuItem>
                        {!isMod && (
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/chatbots/${chatbot.chatbot_id}`, { state: { chatbot, tab: 'moderators' } }); }}
                            className="text-slate-700 focus:text-slate-900 focus:bg-slate-100"
                          >
                            <Users className="w-4 h-4 mr-2" />
                            Moderators
                          </DropdownMenuItem>
                        )}
                        {!isMod && (
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); copyEmbedCode(chatbot); }}
                            className="text-slate-700 focus:text-slate-900 focus:bg-slate-100"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Embed Code
                          </DropdownMenuItem>
                        )}
                        {!isMod && (
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); setDeleteChatbot(chatbot); }}
                            className="text-red-600 focus:text-red-700 focus:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <h3 className="font-semibold text-slate-900 mb-1">{chatbot.name}</h3>
                {isMod && chatbot.owner_org_name && (
                  <p className="text-xs text-purple-600 mb-1">{chatbot.owner_org_name}</p>
                )}
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {chatbot.description || 'No description'}
                </p>

                <div className="flex items-center justify-between">
                  <Badge className={getStatusColor(chatbot.status)}>
                    {chatbot.status}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {new Date(chatbot.created_at).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
            );
          };

          return (
            <div className="space-y-8">
              {ownedList.length > 0 && (
                <section data-testid="chatbots-owned-section">
                  <div className="flex items-baseline justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Owned by me</h2>
                    <span className="text-xs text-slate-500">{ownedList.length}</span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {ownedList.map(renderCard)}
                  </div>
                </section>
              )}
              {moderatingList.length > 0 && (
                <section data-testid="chatbots-moderating-section">
                  <div className="flex items-baseline justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Moderating</h2>
                    <span className="text-xs text-slate-500">{moderatingList.length}</span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {moderatingList.map(renderCard)}
                  </div>
                </section>
              )}
            </div>
          );
        })()
      )}

      {/* Create AI Employee Wizard */}
      <CreateAgentWizard
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          loadData();
        }}
        accounts={accounts}
        subAccounts={subAccounts}
        featureAccess={featureAccess}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteChatbot} onOpenChange={() => setDeleteChatbot(null)}>
        <AlertDialogContent className="bg-white border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete AI Employee</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Are you sure you want to delete &quot;{deleteChatbot?.name}&quot;? This will also delete all knowledge documents and chat history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteChatbot}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChatbotsPage;
